import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Download, Filter, Search } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDate } from "@/lib/dateFormat";

const SCOPE_LABEL = { mine: "Mine", team: "My team", all: "All", unassigned: "No owner", handed_off: "Handed off" };
const STATE_TABS = [
  ["new", "New – never called"], ["worked", "Worked"], ["due_today", "Due today"],
  ["overdue", "Overdue"], ["qualified", "Qualified"], ["lost", "Lost"], ["open", "All open"],
];
const DATE_MEANS = [["lead_date", "Lead date"], ["last_call", "Last call date"], ["next_call", "Next call date"]];

// Mon–Sun IST weeks, months and the two "last" windows the sales team asks for.
// FIX-UP (Fix 1a): the IST calendar day is found by shifting the clock +5:30 and reading it
// ONLY through the UTC getters — the browser's own time zone never enters the maths.
function quickRange(kind) {
  const ist = new Date(Date.now() + 5.5 * 3600 * 1000);
  const y = ist.getUTCFullYear(), m = ist.getUTCMonth(), d = ist.getUTCDate();
  const dow = ist.getUTCDay();
  // Date.UTC rolls day/month overflow itself, so plain arithmetic is safe
  const iso = (yy, mm, dd) => new Date(Date.UTC(yy, mm, dd)).toISOString().slice(0, 10);
  const sinceMonday = (dow + 6) % 7;
  switch (kind) {
    case "today": { const t = iso(y, m, d); return [t, t]; }
    case "yesterday": { const t = iso(y, m, d - 1); return [t, t]; }
    case "this_week": return [iso(y, m, d - sinceMonday), iso(y, m, d - sinceMonday + 6)];
    case "last_week": return [iso(y, m, d - sinceMonday - 7), iso(y, m, d - sinceMonday - 1)];
    case "this_month": return [iso(y, m, 1), iso(y, m, d)];
    case "last_month": return [iso(y, m - 1, 1), iso(y, m, 0)];
    default: return ["", ""];
  }
}

export default function LeadDeskPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ rows: [], total: 0, showing: "", pages: 1, can_see_all: false, can_see_team: false });
  const [counts, setCounts] = useState({});
  const [options, setOptions] = useState({ owners: [], campaigns: [], form_names: [], cities: [], states: [], stages: [] });
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(null);
  const [scope, setScope] = useState(null);           // item 1(a): remembered per user
  const [stateTab, setStateTab] = useState("open");
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState({ by: "created_at", dir: "desc" });
  const [f, setF] = useState({ owner: "", campaign: "", form_name: "", city: "", state: "", stage: "", date_means: "lead_date", date_from: "", date_to: "" });

  const scopeKey = `orbit_lead_tab_${user?.id || user?._id || "me"}`;

  const params = useMemo(() => {
    const p = new URLSearchParams({ tab: scope || "mine", state_tab: stateTab, page: String(page), page_size: String(pageSize), sort_by: sort.by, sort_dir: sort.dir });
    if (search.trim()) p.set("search", search.trim());
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p;
  }, [scope, stateTab, page, pageSize, sort, search, f]);

  const load = useCallback(async () => {
    if (!scope) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/leads/list?${params}`);
      setData(d);
      const cp = new URLSearchParams({ tab: scope, date_means: f.date_means });
      if (search.trim()) cp.set("search", search.trim());
      ["owner", "campaign", "form_name", "city", "state", "stage", "date_from", "date_to"].forEach(k => { if (f[k]) cp.set(k, f[k]); });
      const { data: c } = await api.get(`/leads/list/counts?${cp}`);
      setCounts(c);
      setBlocked(null);
    } catch (e) {
      if (e.response?.status === 403) setBlocked(e.response?.data?.detail || "You cannot see this tab");
      else toast.error(e.response?.data?.detail || "Could not load the leads");
      setData({ rows: [], total: 0, showing: "", pages: 1, can_see_all: false, can_see_team: false });
    } finally { setLoading(false); }
  }, [scope, params, f, search]);

  useEffect(() => {
    api.get("/leads/list?tab=mine&page_size=1").then(r => {
      const saved = window.localStorage.getItem(scopeKey);
      setScope(saved || r.data?.default_tab || "mine");
      setData(d => ({ ...d, can_see_all: r.data?.can_see_all, can_see_team: r.data?.can_see_team }));
    }).catch(() => setScope("mine"));
    api.get("/leads/list/filter-options").then(r => setOptions(r.data)).catch(() => {});
  }, [scopeKey]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (scope) window.localStorage.setItem(scopeKey, scope); }, [scope, scopeKey]);
  useEffect(() => { setPage(1); }, [scope, stateTab, pageSize, search, f]);

  const scopes = ["mine", ...(data.can_see_team ? ["team"] : []), "unassigned", "handed_off", ...(data.can_see_all ? ["all"] : [])];
  const sortOn = (by) => setSort(s => ({ by, dir: s.by === by && s.dir === "desc" ? "asc" : "desc" }));
  const exportXlsx = async () => {
    try {
      const r = await api.get(`/leads/list/export?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Leads_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch { toast.error("Could not export the list"); }
  };
  const setQuick = (kind) => { const [from, to] = quickRange(kind); setF(x => ({ ...x, date_from: from, date_to: to })); };

  const TH = ({ id, children, className = "" }) => (
    <th className={`py-2 px-2 text-left font-semibold cursor-pointer select-none ${className}`}
      onClick={() => sortOn(id)} data-testid={`lead-col-${id}`}>
      {children}{sort.by === id ? (sort.dir === "desc" ? " ↓" : " ↑") : ""}
    </th>
  );

  return (
    <div className="p-6 max-w-[1800px]" data-testid="lead-desk-page">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Lead Desk</h1>
          <p className="text-xs text-zinc-500 mt-0.5" data-testid="lead-showing">{data.showing || "—"}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs" onClick={() => setShowFilters(v => !v)} data-testid="lead-filters-toggle">
            <Filter className="w-3 h-3 mr-1" /> Filters <ChevronDown className="w-3 h-3 ml-1" />
          </Button>
          <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs" onClick={exportXlsx} data-testid="lead-export-btn">
            <Download className="w-3 h-3 mr-1" /> Export
          </Button>
          <Button size="sm" className="rounded-sm h-8 text-xs" onClick={() => navigate("/leads/new")} data-testid="lead-quick-add-btn">Quick Add</Button>
        </div>
      </div>

      <div className="flex gap-1 mb-2 flex-wrap" data-testid="lead-scope-tabs">
        {scopes.map(s => (
          <button key={s} onClick={() => setScope(s)} data-testid={`lead-scope-${s}`}
            className={`px-3 h-8 text-xs rounded-sm border ${scope === s ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-200 text-zinc-600"}`}>
            {SCOPE_LABEL[s]}
          </button>
        ))}
      </div>

      <div className="flex gap-1 mb-3 flex-wrap" data-testid="lead-state-tabs">
        {STATE_TABS.map(([k, label]) => (
          <button key={k} onClick={() => setStateTab(k)} data-testid={`lead-tab-${k}`}
            className={`px-3 h-8 text-xs rounded-sm border ${stateTab === k ? "border-zinc-900 bg-zinc-100 font-semibold" : "border-zinc-200 bg-white text-zinc-600"}`}>
            {label} <span className="font-mono ml-1" data-testid={`lead-count-${k}`}>{counts[k] ?? "—"}</span>
          </button>
        ))}
      </div>

      {showFilters && (
        <div className="border border-zinc-200 rounded-sm p-3 mb-3 bg-zinc-50" data-testid="lead-filters">
          <div className="flex flex-wrap gap-1 mb-2">
            {[["today", "Today"], ["yesterday", "Yesterday"], ["this_week", "This week"], ["last_week", "Last week"], ["this_month", "This month"], ["last_month", "Last month"]].map(([k, l]) => (
              <Button key={k} size="sm" variant="outline" className="h-7 text-[11px] rounded-sm" onClick={() => setQuick(k)} data-testid={`lead-quick-${k}`}>{l}</Button>
            ))}
            <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm" onClick={() => setF(x => ({ ...x, date_from: "", date_to: "" }))} data-testid="lead-quick-clear">Clear dates</Button>
            {f.date_from && f.date_to && (
              <span className="self-center text-[11px] font-mono text-zinc-600" data-testid="lead-range-text">
                {fmtDate(f.date_from)} → {fmtDate(f.date_to)}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <div><Label className="text-[10px] uppercase text-zinc-500">Date means</Label>
              <select value={f.date_means} onChange={e => setF(x => ({ ...x, date_means: e.target.value }))} className="h-8 w-full border border-zinc-200 rounded-sm text-xs px-1 bg-white" data-testid="lead-date-means">
                {DATE_MEANS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select></div>
            <div><Label className="text-[10px] uppercase text-zinc-500">From</Label>
              <Input type="date" value={f.date_from} onChange={e => setF(x => ({ ...x, date_from: e.target.value }))} className="h-8 text-xs rounded-sm" data-testid="lead-date-from" /></div>
            <div><Label className="text-[10px] uppercase text-zinc-500">To</Label>
              <Input type="date" value={f.date_to} onChange={e => setF(x => ({ ...x, date_to: e.target.value }))} className="h-8 text-xs rounded-sm" data-testid="lead-date-to" /></div>
            {options.can_filter_owner && (
              <div><Label className="text-[10px] uppercase text-zinc-500">Owner</Label>
                <select value={f.owner} onChange={e => setF(x => ({ ...x, owner: e.target.value }))} className="h-8 w-full border border-zinc-200 rounded-sm text-xs px-1 bg-white" data-testid="lead-filter-owner">
                  <option value="">Any owner</option>
                  {(options.owners || []).map(o => <option key={o.user_id} value={o.user_id}>{o.name}</option>)}
                </select></div>
            )}
            {[["campaign", "Campaign", options.campaigns], ["form_name", "Form name", options.form_names], ["city", "City", options.cities], ["state", "State", options.states]].map(([key, label, list]) => (
              <div key={key}><Label className="text-[10px] uppercase text-zinc-500">{label}</Label>
                <select value={f[key]} onChange={e => setF(x => ({ ...x, [key]: e.target.value }))} className="h-8 w-full border border-zinc-200 rounded-sm text-xs px-1 bg-white" data-testid={`lead-filter-${key}`}>
                  <option value="">Any</option>
                  {(list || []).map(v => <option key={v} value={v}>{v}</option>)}
                </select></div>
            ))}
            <div><Label className="text-[10px] uppercase text-zinc-500">Stage</Label>
              <select value={f.stage} onChange={e => setF(x => ({ ...x, stage: e.target.value }))} className="h-8 w-full border border-zinc-200 rounded-sm text-xs px-1 bg-white" data-testid="lead-filter-stage">
                <option value="">Any stage</option>
                {(options.stages || []).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select></div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-zinc-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name, company or phone"
            className="h-8 pl-7 text-xs rounded-sm" data-testid="lead-search" />
        </div>
        <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} className="h-8 border border-zinc-200 rounded-sm text-xs px-1 bg-white" data-testid="lead-page-size">
          {[25, 50, 100].map(n => <option key={n} value={n}>{n} per page</option>)}
        </select>
      </div>

      {blocked ? (
        <div className="border border-amber-200 bg-amber-50 rounded-sm p-4 text-xs text-amber-800" data-testid="lead-blocked">{blocked}</div>
      ) : loading ? (
        <p className="text-xs text-zinc-400 py-6">Loading…</p>
      ) : (
        <>
          <div className="border border-zinc-200 rounded-sm overflow-x-auto bg-white">
            <table className="w-full text-xs" data-testid="lead-table">
              <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase text-zinc-500">
                <tr>
                  <TH id="name">Name</TH><TH id="company">Company</TH>
                  <th className="py-2 px-2 text-left font-semibold">Phone</th>
                  <TH id="city">City</TH><TH id="state">State</TH>
                  <th className="py-2 px-2 text-left font-semibold">Owner</th>
                  <th className="py-2 px-2 text-left font-semibold">Campaign</th>
                  <TH id="lead_date">Lead date</TH>
                  <th className="py-2 px-2 text-right font-semibold">Age</th>
                  <TH id="stage">Stage</TH>
                  <TH id="calls" className="text-right">Calls</TH>
                  <TH id="last_call">Last called</TH>
                  <TH id="next_call">Next call</TH>
                  <TH id="kind">Kind</TH>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).map(l => {
                  const overdue = l.next_call_at && l.next_call_at < new Date().toISOString();
                  return (
                    <tr key={l._id} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                      onClick={() => navigate(`/leads/${l._id}`)} data-testid={`lead-row-${l.name}`}>
                      <td className="py-1.5 px-2 font-medium">{l.name}
                        {!l.calls_count && <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-zinc-200 text-zinc-600 rounded-sm" data-testid="never-called-badge">Never called</span>}
                      </td>
                      <td className="py-1.5 px-2">{l.company || "—"}</td>
                      <td className="py-1.5 px-2 font-mono">{(l.phones || []).join(", ") || "—"}</td>
                      <td className="py-1.5 px-2">{l.city || "—"}</td>
                      <td className="py-1.5 px-2">{l.state || <span className="text-amber-600">not set</span>}</td>
                      <td className="py-1.5 px-2">{l.owner_name || <span className="text-amber-600">No owner</span>}</td>
                      <td className="py-1.5 px-2">{(l.marketing_source || {}).campaign || "—"}</td>
                      <td className="py-1.5 px-2 font-mono">{fmtDate(l.lead_date)}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{l.lead_age_days}</td>
                      <td className="py-1.5 px-2 capitalize">{l.pipeline_stage}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{l.calls_count || 0}</td>
                      <td className="py-1.5 px-2">{l.last_call_at ? `${fmtDate(l.last_call_at.slice(0, 10))} · ${l.last_call_outcome || ""}` : "—"}</td>
                      <td className={`py-1.5 px-2 font-mono ${overdue ? "text-red-600 font-semibold" : ""}`} data-testid={`lead-next-call-${l.name}`}>
                        {l.next_call_at ? fmtDate(l.next_call_at.slice(0, 10)) : "—"}
                      </td>
                      <td className="py-1.5 px-2 capitalize">{l.lead_kind}</td>
                    </tr>
                  );
                })}
                {(data.rows || []).length === 0 && (
                  <tr><td colSpan={14} className="py-6 text-center text-zinc-400" data-testid="lead-empty">No leads here</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-[11px] text-zinc-500" data-testid="lead-showing-bottom">{data.showing}</p>
            <div className="flex gap-1 items-center">
              <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm" disabled={page <= 1}
                onClick={() => setPage(p => p - 1)} data-testid="lead-prev-page">Previous</Button>
              <span className="text-[11px] text-zinc-500 px-2" data-testid="lead-page-label">Page {data.page} of {data.pages}</span>
              <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm" disabled={page >= (data.pages || 1)}
                onClick={() => setPage(p => p + 1)} data-testid="lead-next-page">Next</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
